import { ASSESSMENT_TYPE_ID, DATE_FORMAT } from 'constants/constants';
import { differenceInBusinessDays, format } from 'date-fns';
import isEmpty from 'lodash/isEmpty';
import { useMemo } from 'react';
import { convertUTCDateToLocalDate, relativeDateTimeFormat } from 'utils/dateAndTimeUtils';
import { useAppSetting } from './useAppSetting';

const statusByDueDate = (asm: AssessmentAPI, ddThreshold: number): DueDateStatus => {
  if (!asm.DueDate || asm.AssessmentStage === 'Completed' || ddThreshold < 0) return undefined;

  const dueDate = asm.DueDate ? new Date(convertUTCDateToLocalDate(asm.DueDate)) : null;
  if (!dueDate) return undefined;

  if (differenceInBusinessDays(dueDate, new Date()) < 0) return 'overDue';
  if (differenceInBusinessDays(dueDate, new Date()) <= ddThreshold) return 'nearDue';

  return undefined;
};

const colorByDueDateStatus = (status?: DueDateStatus): string | undefined => {
  if (status === 'overDue') return '#852C2C';
  if (status === 'nearDue') return '#F5B335';
  return undefined;
};

const asmTypeText = (asm: AssessmentAPI): string => {
  if (asm.AssessmentTypeId === ASSESSMENT_TYPE_ID.PRE_ASSESSMENT) return asm.AssessmentType;
  return 'Control Assessment';
};

export function useAssessmentList({ assessmentList, maxActiveAssessment }: Readonly<UseAssessmentListProps>): UseAssessmentListReturn {
  const dueDateThresholdFetcher = useAppSetting('AssessmentOverview_DueDateThresholdBizDays');
  const dueDateThreshold = Number(dueDateThresholdFetcher?.data ?? -1);

  const asmList = useMemo(() => {
    if (isEmpty(assessmentList)) return [];

    const seqNoBaseNumber = maxActiveAssessment ?? assessmentList.length;

    return assessmentList.map((asm, index) => {
      const dueDate = asm.DueDate ? new Date(convertUTCDateToLocalDate(asm.DueDate)) : null;
      const dueDateText = dueDate ? format(dueDate, DATE_FORMAT) : undefined;

      const dueDateStatus = statusByDueDate(asm, dueDateThreshold);
      const dueDateColor = colorByDueDateStatus(dueDateStatus);

      const assessmentTypeText = asmTypeText(asm);

      return {
        ...asm,
        seqNo: `${index + 1}/${seqNoBaseNumber}`,
        dueDateText,
        dueDateStatus,
        dueDateColor,
        assessmentTypeText,
        assessmentIdentifierText: asm.AssessmentIdentifier || asm.AssessmentId || '-',
        shouldShowAssessmentType: !isEmpty(asm.AssessmentType) && asm.Source === 'Portal 2.0',
        relativeLastUpdated: relativeDateTimeFormat(asm.LastUpdated),
      };
    });
  }, [assessmentList, dueDateThreshold, maxActiveAssessment]);

  return asmList;
}

type UseAssessmentListProps = {
  assessmentList: AssessmentAPI[];
  maxActiveAssessment?: number;
};

type UseAssessmentListReturn = Assessment[];

export type Assessment = AssessmentAPI & {
  seqNo: string; // calculated on FE for display purposes
  dueDateText?: string; // formatted date string
  dueDateStatus: DueDateStatus; // 'overDue' | 'nearDue' | undefined
  dueDateColor?: string; // color code for the due date status
  assessmentTypeText: string; // text representation of the assessment type
  assessmentIdentifierText: string | number; // text representation of the assessment identifier
  shouldShowAssessmentType: boolean; // flag to indicate if the assessment type should be displayed
  relativeLastUpdated?: string; // relative time since last updated
};

type DueDateStatus = 'overDue' | 'nearDue' | undefined;

// =========================================================

type AssessmentAPI = {
  AssessmentId: number;
  AssessmentName: string;
  Requester: string;
  AssessmentIdentifier?: string;
  Status: string;
  AssessmentStatusId: number;
  AssessmentType: string;
  AssessmentTypeId?: string;
  AssessmentStage: Stage;
  UrgencyId: number;
  Owner: string;
  AssessmentRequests: AssessmentRequest[];
  AssessmentQuestionnaires: AssessmentQuestionnaire[];
  QuestionnaireProgress: number;
  Vendor: Vendor;
  Product?: Product;
  DueDate?: string;
  LastUpdated: string;
  IsEscalated?: boolean;
  IsPaused?: boolean;
  IsExpedite?: boolean;
  IsValidatedAssessment?: boolean;
  PublishedResult?: AssessmentPublishedResult;
  Source?: 'Portal 2.0' | 'Dynamic'; // Portal 2.0 or Dynamic
  DisplayResultAndEvidenceLinks?: number;
};

type AssessmentRequest = {
  AssessmentRequestId: number;
  AssessmentRequestDate: string;
  AssessmentRequestCategoryId: number;
  AssessmentRequestTypeId: number;
  ClientOrgId: number;
  Customer: Vendor;
};

type AssessmentQuestionnaire = {
  QuestionnaireTemplateId: number;
  QuestionnaireTemplateName: string;
  AssessmentQuestionnaireId: number;
  Description: string;
  QuestionnaireTemplateTypeId: number;
  QuestionnaireTemplateType: string;
  ReferenceId: number;
  StatusId: number;
  QuestionnaireTemplateStatus: string;
  QuestionnaireTemplateSubTypeId: number;
  QuestionnaireTemplateSubType: string;
  VersionNumber: string;
  VendorOrgId: number;
  MandatoryQuestionResponses: number;
  MandatoryQuestions: number;
  IsVendorSubmitted?: boolean;
};

type Vendor = {
  OrgId: number;
  OrgName: string;
  DomainName: string;
};

type Product = {
  ProductVendorId: number;
  ProductId: string;
  ProductName: string;
};

type AssessmentPublishedResult = {
  RiskRatingId: number;
  ImpactLevelId: number;
  LikelihoodId: number;
};

const STAGE = ['Active', 'Remediation', 'Completed'] as const;
type Stage = (typeof STAGE)[number];
